import { Check, Clock, MessageChatSquare, Play, XCircle } from "@untitledui/icons";
import { useState } from "react";
import { useMessageTemplates } from "@/api/message-templates";
import { useCancelSequenceRun, useSequenceRun } from "@/api/sequences";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCreatorId } from "@/utils/formatters";
import { toTitleCase } from "@/utils/toTitleCase";

const STATUS_COLORS: Record<string, "success" | "error" | "warning" | "gray"> = {
  active: "warning",
  completed: "success",
  cancelled: "gray",
  pending: "gray",
  failed: "error",
  skipped: "gray",
};

function stepLabel(step: { type: string; messageTemplateId: string | null; customMessage: string | null }) {
  if (step.type === "send_message")
    return step.customMessage && !step.messageTemplateId ? "Send Custom Message" : "Send Message";
  if (step.type === "remove_from_collab") return "Remove from Collaboration";
  return step.type;
}

function StepIcon({ type }: { type: string }) {
  if (type === "send_message") return <MessageChatSquare className="w-5 h-5 text-tertiary" />;
  return <XCircle className="w-5 h-5 text-tertiary" />;
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface SequenceRunDialogProps {
  runId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function SequenceRunDialog({ runId, onOpenChange }: SequenceRunDialogProps) {
  const { data: run, isLoading } = useSequenceRun(runId);
  const { data: templates = [] } = useMessageTemplates();
  const cancelRun = useCancelSequenceRun(runId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <Dialog open={!!runId} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between gap-4 pr-6">
            <div className="flex-1">
              <DialogTitle>
                {run
                  ? `${run.sequenceName} - ${formatCreatorId(run.username ?? run.creatorId ?? "Unknown")}`
                  : "Sequence Run"}
              </DialogTitle>
              <DialogDescription>
                {run
                  ? `${run.steps.filter((s) => s.status === "completed").length}/${run.steps.length} steps completed`
                  : "Loading..."}
              </DialogDescription>
            </div>
            {run && (
              <div className="flex items-center gap-2">
                <Badge size="sm" color={STATUS_COLORS[run.status] ?? "gray"}>
                  {toTitleCase(run.status)}
                </Badge>
                <Button
                  color="primary-destructive"
                  size="sm"
                  isDisabled={run.status !== "active"}
                  onClick={() => setConfirmOpen(true)}
                >
                  Cancel Run
                </Button>
              </div>
            )}
          </DialogHeader>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoadingIndicator />
            </div>
          )}

          {run && (
            <div className="rounded-lg bg-secondary p-6">
              <div className="flex flex-col items-center">
                <div className="sequence-steps relative flex flex-col w-full max-w-md">
                  {/* Trigger row */}
                  <div className="flex items-start gap-3">
                    <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full bg-gray-800 text-white shrink-0 mt-2.5">
                      <Play className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex items-center flex-1 rounded-lg border border-gray-200 bg-primary shadow-sm px-4 py-3">
                      <span className="text-sm font-medium text-secondary flex-1">Triggered</span>
                      <span className="text-xs text-tertiary">{formatDate(run.createdAt)}</span>
                    </div>
                  </div>

                  {/* Steps */}
                  {run.steps.map((step, index) => (
                    <div key={step.id}>
                      {/* Delay row — only for unscheduled steps */}
                      {!step.scheduled && (
                        <div className="flex items-center gap-3 py-6">
                          <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 bg-primary text-tertiary shrink-0">
                            <Clock className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex items-center flex-1 rounded-lg border border-gray-200 bg-primary shadow-sm px-4 py-2">
                            <span className="text-sm font-medium text-tertiary">
                              {step.delayDays === 0
                                ? "Immediately"
                                : `Wait ${step.delayDays} ${step.delayDays === 1 ? "day" : "days"}`}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Connector for scheduled steps */}
                      {step.scheduled && <div className="h-6" />}

                      {/* Step card */}
                      <div className="flex items-start gap-3">
                        <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full bg-gray-800 text-white text-xs font-medium shrink-0 mt-2.5">
                          {index + 1}
                        </div>
                        <div className="flex flex-col flex-1 rounded-lg border border-gray-200 bg-primary shadow-sm">
                          <div className="flex items-center gap-2.5 px-4 py-3">
                            <StepIcon type={step.type} />
                            <span className="text-sm font-medium text-secondary flex-1">{stepLabel(step)}</span>
                            {step.scheduled && step.status ? (
                              <Badge size="sm" color={STATUS_COLORS[step.status] ?? "gray"}>
                                {toTitleCase(step.status)}
                              </Badge>
                            ) : !step.scheduled && run.status !== "active" ? (
                              <Badge size="sm" color="gray">
                                Skipped
                              </Badge>
                            ) : null}
                          </div>
                          {/* Due/executed time for scheduled steps */}
                          {step.scheduled && (step.dueAt || step.executedAt) && (
                            <div className="border-t border-gray-200 px-4 py-2">
                              {step.executedAt ? (
                                <p className="text-xs text-tertiary">Executed {formatDate(step.executedAt)}</p>
                              ) : step.dueAt ? (
                                <p className="text-xs text-tertiary">Due {formatDate(step.dueAt)}</p>
                              ) : null}
                            </div>
                          )}
                          {step.type === "send_message" && (step.messageTemplateId || step.customMessage) && (
                            <div className="border-t border-gray-200 px-4 py-3">
                              {step.messageTemplateId ? (
                                <p className="text-xs text-tertiary mb-1">
                                  Template: {templates.find((t) => t.id === step.messageTemplateId)?.name ?? "Unknown"}
                                </p>
                              ) : (
                                <p className="text-xs text-tertiary mb-1">Custom Message</p>
                              )}
                              <p className="text-sm text-secondary whitespace-pre-wrap break-words">
                                {step.messageTemplateId
                                  ? (templates.find((t) => t.id === step.messageTemplateId)?.content ?? "")
                                  : step.customMessage}
                              </p>
                            </div>
                          )}
                          {step.reason && (
                            <div className="border-t border-gray-200 px-4 py-3">
                              <p
                                className={`text-xs ${step.status === "failed" ? "text-utility-error-600" : "text-tertiary"}`}
                              >
                                {step.reason}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* End of sequence */}
                  <div className="flex items-center gap-3 pt-6">
                    <div className="sequence-badge flex items-center justify-center w-7 h-7 rounded-full bg-gray-800 text-white shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex items-center flex-1 rounded-lg border border-gray-200 bg-primary shadow-sm px-4 py-3">
                      <span className="text-sm font-medium text-tertiary flex-1">End of Sequence</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Run</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this sequence run? Any pending steps will not be executed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => setConfirmOpen(false)}>
              Go Back
            </Button>
            <Button
              color="primary-destructive"
              size="sm"
              isLoading={cancelRun.isPending}
              onClick={async () => {
                await cancelRun.mutateAsync();
                setConfirmOpen(false);
              }}
            >
              Cancel Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
